import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, ArrowUpDown, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { materialIssuesApi, clientsApi, itemsApi } from '../api/api';

const today = () => new Date().toISOString().split('T')[0];
const EMPTY_FORM = { issue_number:'', client_id:'', issue_date: today(), notes:'' };
const EMPTY_LINE = { item_id:'', quantity:1, unit_price:0 };

function LineItemsEditor({ items, lines, setLines }) {
  const addLine = () => setLines(l => [...l, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));
  const total = lines.reduce((s, l) => s + (Number(l.quantity) * Number(l.unit_price)), 0);

  return (
    <div>
      <div className="section-label">Items to Issue</div>
      <table className="line-items-table">
        <thead><tr>
          <th style={{width:'36%'}}>Item</th>
          <th style={{width:'16%'}}>Issue Qty</th>
          <th style={{width:'20%'}}>Unit Price</th>
          <th style={{width:'18%'}}>Total / Stock</th>
          <th style={{width:'10%'}}></th>
        </tr></thead>
        <tbody>
          {lines.map((ln, i) => {
            const item = items.find(it => String(it.id) === String(ln.item_id));
            const overStock = item && Number(ln.quantity) > item.stock_qty;
            return (
              <tr key={i}>
                <td>
                  <select value={ln.item_id} onChange={e => {
                    const it = items.find(x => String(x.id) === e.target.value);
                    setLine(i, 'item_id', e.target.value);
                    if (it) setLine(i, 'unit_price', it.unit_price);
                  }}>
                    <option value="">-- Select Item --</option>
                    {items.map(it => <option key={it.id} value={it.id}>{it.item_code} — {it.name} ({it.stock_qty} {it.unit})</option>)}
                  </select>
                </td>
                <td>
                  <input type="number" min="1" value={ln.quantity}
                    style={{borderColor: overStock ? 'var(--red)' : undefined}}
                    onChange={e => setLine(i, 'quantity', e.target.value)} />
                </td>
                <td><input type="number" min="0" step="0.01" value={ln.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} /></td>
                <td style={{textAlign:'right', fontFamily:'var(--mono)', fontSize:12}}>
                  ₹{(Number(ln.quantity) * Number(ln.unit_price)).toLocaleString('en-IN')}
                  {item && <div style={{fontSize:10, color: overStock ? 'var(--red)' : 'var(--gray-400)', fontWeight: overStock ? 600 : 400}}>
                    {overStock ? `⚠ Only ${item.stock_qty} in stock` : `Stock: ${item.stock_qty} ${item.unit}`}
                  </div>}
                </td>
                <td><button type="button" className="btn-icon danger" onClick={() => removeLine(i)}><Trash2 size={12} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10}}>
        <button type="button" className="btn btn-sm" onClick={addLine}><Plus size={12} /> Add Line</button>
        <div style={{fontFamily:'var(--mono)', fontWeight:600, fontSize:14}}>
          Total: ₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}
        </div>
      </div>
    </div>
  );
}

function ViewModal({ issueId, onClose }) {
  const { data: mi, isLoading } = useQuery({ queryKey: ['issue', issueId], queryFn: () => materialIssuesApi.getOne(issueId) });
  if (isLoading) return (
    <div className="modal-overlay"><div className="modal modal-lg"><div className="modal-body"><div className="spinner" /></div></div></div>
  );
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>Material Issue — {mi.issue_number}</h2>
          <span className={`badge badge-${mi.status}`}>{mi.status}</span>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-grid" style={{marginBottom:16}}>
            <div><div className="form-label">Client</div><strong>{mi.client_name}</strong></div>
            <div><div className="form-label">Client Code</div>{mi.client_code}</div>
            <div><div className="form-label">Issue Date</div>{new Date(mi.issue_date).toLocaleDateString('en-IN')}</div>
            <div><div className="form-label">Phone</div>{mi.client_phone || '—'}</div>
          </div>
          <div className="section-label">Items Issued</div>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead><tr style={{background:'var(--gray-50)'}}>
              <th style={{padding:'8px 12px', textAlign:'left', fontSize:11, color:'var(--gray-400)', textTransform:'uppercase'}}>Item</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Qty</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Unit</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Price</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Total</th>
            </tr></thead>
            <tbody>
              {(mi.items||[]).map((it, i) => (
                <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 12px'}}>{it.item_code} — {it.item_name}</td>
                  <td style={{padding:'8px 12px', textAlign:'right'}}>{it.quantity}</td>
                  <td style={{padding:'8px 12px', textAlign:'right'}}>{it.unit}</td>
                  <td style={{padding:'8px 12px', textAlign:'right', fontFamily:'var(--mono)'}}>₹{Number(it.unit_price).toLocaleString('en-IN')}</td>
                  <td style={{padding:'8px 12px', textAlign:'right', fontFamily:'var(--mono)'}}>₹{Number(it.total_price).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={4} style={{padding:'10px 12px', textAlign:'right', fontWeight:600}}>Grand Total</td>
              <td style={{padding:'10px 12px', textAlign:'right', fontFamily:'var(--mono)', fontWeight:600}}>₹{Number(mi.total_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
            </tr></tfoot>
          </table>
          {mi.notes && <div style={{marginTop:14}}><div className="form-label">Notes</div><p style={{fontSize:13}}>{mi.notes}</p></div>}
        </div>
        <div className="modal-footer"><button className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

export default function MaterialIssues() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lines, setLines] = useState([{...EMPTY_LINE}]);

  const { data: issues = [], isLoading } = useQuery({
    queryKey: ['material-issues', search, statusFilter],
    queryFn: () => materialIssuesApi.getAll({ search, status: statusFilter }),
  });
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.getAll({}) });
  const { data: items = [] } = useQuery({ queryKey: ['items-all'], queryFn: () => itemsApi.getAll({ status: 'active' }) });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? materialIssuesApi.update(editing.id, d) : materialIssuesApi.create(d),
    onSuccess: () => {
      qc.invalidateQueries(['material-issues']); qc.invalidateQueries(['dashboard']);
      qc.invalidateQueries(['items']); qc.invalidateQueries(['items-all']);
      toast.success(editing ? 'Issue updated' : 'Material issued successfully');
      closeModal();
    },
    onError: (e) => toast.error(e.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: materialIssuesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['material-issues']); qc.invalidateQueries(['items-all']); toast.success('Issue deleted, stock restored'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const openAdd = async () => {
    const res = await materialIssuesApi.nextNumber();
    setEditing(null); setForm({...EMPTY_FORM, issue_number: res.issue_number}); setLines([{...EMPTY_LINE}]); setModal(true);
  };
  const openEdit = (mi) => {
    setEditing(mi);
    setForm({ issue_number: mi.issue_number, client_id: mi.client_id, issue_date: mi.issue_date?.split('T')[0]||today(), notes: mi.notes||'', status: mi.status });
    setModal(true);
  };
  const closeModal = () => { setModal(false); setEditing(null); };
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = (e) => {
    e.preventDefault();
    if (!editing && (!lines.length || lines.some(l => !l.item_id))) return toast.error('Add at least one valid line item');
    saveMutation.mutate(editing ? { status: form.status, notes: form.notes } : { ...form, items: lines });
  };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Material Issues</span>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> New Issue</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <ArrowUpDown size={15} />
            <span className="card-title">Material Issues ({issues.length})</span>
            <div className="search-wrap">
              <Search /><input placeholder="Search issue number, client..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="returned">Returned</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr><th>Issue No.</th><th>Client</th><th>Issue Date</th><th>Total Amount</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {issues.length === 0 && <tr><td colSpan={6}><div className="empty-state"><ArrowUpDown /><p>No material issues found</p></div></td></tr>}
                  {issues.map(mi => (
                    <tr key={mi.id}>
                      <td className="mono">{mi.issue_number}</td>
                      <td style={{fontWeight:500}}>{mi.client_name}</td>
                      <td>{new Date(mi.issue_date).toLocaleDateString('en-IN')}</td>
                      <td className="amount">₹{Number(mi.total_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                      <td><span className={`badge badge-${mi.status}`}>{mi.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-icon" title="View" onClick={() => setViewId(mi.id)}><Eye /></button>
                          <button className="btn-icon" title="Edit Status" onClick={() => openEdit(mi)}><Pencil /></button>
                          {/* <button className="btn-icon danger" onClick={() => { if(window.confirm('Delete this issue? Stock will be restored.')) deleteMutation.mutate(mi.id); }}><Trash2 /></button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {viewId && <ViewModal issueId={viewId} onClose={() => setViewId(null)} />}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{editing ? 'Update Issue Status' : 'New Material Issue'}</h2>
              <button className="btn btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                {!editing && (
                  <div className="alert alert-warning" style={{marginBottom:14}}>
                    Stock will be automatically deducted when material is issued.
                  </div>
                )}
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Issue Number *</label>
                    <input className="form-control" required value={form.issue_number} onChange={e => set('issue_number', e.target.value)} disabled={!!editing} />
                  </div>
                  {!editing && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Client *</label>
                        <select className="form-control" required value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                          <option value="">-- Select Client --</option>
                          {clients.map(c => <option key={c.id} value={c.id}>{c.client_code} — {c.name}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Issue Date *</label>
                        <input className="form-control" type="date" required value={form.issue_date} onChange={e => set('issue_date', e.target.value)} />
                      </div>
                    </>
                  )}
                  {editing && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="issued">Issued</option>
                        <option value="returned">Returned</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group form-full">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                  </div>
                </div>
                {!editing && <LineItemsEditor items={items} lines={lines} setLines={setLines} />}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Processing...' : editing ? 'Update' : 'Issue Material'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}